import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';

import { AfterLoginRedirect } from 'src/providers/auth/afterLoginRedirect';
import { Auth } from 'src/providers/auth/auth';
import { Booking } from 'src/providers/booking/booking';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { JobPage } from 'src/pages/schedule/job/job';

@Injectable({
  providedIn: 'root'
})
export class BookingRedirectGuard implements CanActivate {

  private afterLoginRedirect = inject(AfterLoginRedirect);
  private auth = inject(Auth);
  private booking = inject(Booking);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private windowService = inject(WindowService);
  private storage = inject(TidyStorage);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const bookingId = route.params.bookingId.toString();

    if (!this.auth.isAuthenticated()) {
      this.afterLoginRedirect.pendingBookingId = bookingId;
      const params = {
        bookingId
      }
      this.navCtrl.navigateRoot('login', params);
      return false;
    }

    return true;
  }

  async handleRedirect(route: ActivatedRouteSnapshot): Promise<void> {
    const bookingId = route.params.bookingId.toString();
    const currentBookingId = localStorage.getItem('bookingId');

    const bookingIdChanged = currentBookingId !== bookingId;

    localStorage.setItem('cameFromBookingLink', 'true');
    localStorage.setItem('bookingId', bookingId);

    const path = await this.booking.getBookingPath(bookingId);
    const routeString = path.split('/')[0];
    const bookingRedirectParams = await this.storage.retrieve('bookingRedirectParams');

    if (this.windowService.isDesktopRes && routeString === 'job') {
      localStorage.setItem('shouldOpenBookingPage', 'true');

      this.navCtrl.navigateRoot('schedule');

      setTimeout(async () => {
        const isRightSidePanelOpen = await this.storage.retrieve('dialog-right-side-open');
        if (isRightSidePanelOpen) {
          this.rightSidePanelService.navigateTo(path, bookingRedirectParams, JobPage);
        } else {
          await this.storage.save('dialog-right-side-open', true);
          await this.storage.save('dialog-params', bookingRedirectParams);
          this.rightSidePanelService.openRightSidePanel(JobPage);
        }
      }, 100);
    } else {
      await this.storage.save('dialog-params', bookingRedirectParams);

      if (bookingIdChanged || !this.windowService.isDesktopRes) {
        window.location.href = `/${path}`;
      } else {
        this.navCtrl.navigateRoot(path);
      }
    }
  }
}

export const bookingRedirectGuardFactory = () => new BookingRedirectGuard();
